import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Sparkles } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { formatError } from "@/lib/api";
import { toast } from "sonner";

const DEMOS = [
  { role: "Super Admin", email: "abi@yousolved.com" },
  { role: "HR Admin", email: "hr@alfredaihire.com" },
  { role: "Recruiter", email: "recruiter@alfredaihire.com" },
  { role: "Hiring Manager", email: "manager@alfredaihire.com" },
  { role: "Employee", email: "employee@alfredaihire.com" },
  { role: "Candidate", email: "candidate@alfredaihire.com" },
  { role: "Platform Owner", email: "admin@alfredaihire.com" },
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const routeAfterLogin = (u) => {
    if (u?.role === "CANDIDATE") return "/candidate";
    if (u?.role === "EMPLOYEE") return "/me";
    return "/app/dashboard";
  };

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      nav(routeAfterLogin(u));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  const loginAsDemo = async (demoEmail) => {
    setEmail(demoEmail); setPassword("Alfred!2026");
    setBusy(true);
    try {
      const u = await login(demoEmail, "Alfred!2026");
      toast.success("Signed in as demo user");
      nav(routeAfterLogin(u));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hero-gradient relative hidden lg:flex items-center justify-center p-12">
        <div className="grain" />
        <div className="relative max-w-md text-white">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <Logo variant="mark-dark" size={32} />
            <span className="font-bold text-lg">alfredHR</span>
          </Link>
          <h1 className="text-4xl font-bold leading-tight mb-4">Hire, onboard and stay compliant — all in one place.</h1>
          <p className="text-white/80">Sign in to your workspace or try one of the demo roles to explore RBAC.</p>
          <div className="mt-8 space-y-2">
            <div className="text-xs uppercase tracking-widest text-white/60">Demo shortcuts</div>
            {DEMOS.map(d => (
              <button key={d.email} onClick={() => loginAsDemo(d.email)} disabled={busy}
                data-testid={`demo-login-${d.role.toLowerCase().replace(/\s+/g,'-')}`}
                className="w-full text-left rounded-lg px-3 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 text-sm flex justify-between items-center">
                <span className="font-medium">{d.role}</span>
                <span className="text-white/60 text-xs font-mono">{d.email}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-white">
        <Card className="w-full max-w-md p-8 border shadow-sm">
          <div className="mb-6">
            <h2 className="text-2xl font-bold" data-testid="login-title">Sign in to Alfred</h2>
            <p className="text-sm text-neutral-500 mt-1">Use your email and password, or continue with Google.</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="you@company.com" className="mt-1.5 focus-ring" />
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required placeholder="••••••••" className="mt-1.5 focus-ring" />
            </div>
            <Button type="submit" data-testid="login-submit-btn" disabled={busy} className="btn-primary w-full rounded-full py-6 focus-ring">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <div className="flex items-center gap-3 my-6">
            <div className="h-px bg-neutral-200 flex-1" />
            <span className="text-xs text-neutral-500 uppercase tracking-wider">or</span>
            <div className="h-px bg-neutral-200 flex-1" />
          </div>
          <Button variant="outline" data-testid="google-signin-btn" onClick={googleSignIn} className="w-full rounded-full py-6">
            Continue with Google
          </Button>

          {/* Mobile-only demo shortcuts (desktop version lives in the hero panel) */}
          <div className="mt-6 lg:hidden" data-testid="mobile-demo-shortcuts">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-px bg-neutral-200 flex-1" />
              <span className="text-xs text-neutral-500 uppercase tracking-wider">Try a demo role</span>
              <div className="h-px bg-neutral-200 flex-1" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {DEMOS.map(d => (
                <button
                  key={d.email}
                  onClick={() => loginAsDemo(d.email)}
                  disabled={busy}
                  data-testid={`mobile-demo-login-${d.role.toLowerCase().replace(/\s+/g,'-')}`}
                  className="text-left rounded-lg px-3 py-2 border border-neutral-200 hover:border-pink hover:bg-pink/5 text-xs transition-colors"
                >
                  <div className="font-semibold text-neutral-900 truncate">{d.role}</div>
                  <div className="text-[10px] text-neutral-500 font-mono truncate">{d.email.split("@")[0]}</div>
                </button>
              ))}
            </div>
            <div className="text-[10px] text-neutral-400 text-center mt-2">Password: <span className="font-mono">Alfred!2026</span></div>
          </div>

          <div className="text-sm text-center mt-6 text-neutral-500">
            Don't have an account? <Link to="/signup" className="text-pink font-semibold">Sign up</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
